'use client'

import {
    NumberSearchHit,
    NumberSearchType,
    searchByClient,
    searchByNumber,
} from '@/lib/api/global-search/global-search-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { ReactNode, useState } from 'react'
import {
    MdBiotech,
    MdClose,
    MdEventNote,
    MdMedicalServices,
    MdPerson,
    MdReceipt,
    MdSearch,
    MdTag,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

interface Props {
    onClose: () => void
}

const TYPE_ICON: Record<NumberSearchType, ReactNode> = {
    EXAM: <MdBiotech className="text-info text-xl" />,
    PROCEDURE: <MdMedicalServices className="text-warning text-xl" />,
    CONSULTATION: <MdEventNote className="text-secondary text-xl" />,
    BILLING: <MdReceipt className="text-success text-xl" />,
}

const TYPE_BADGE: Record<NumberSearchType, string> = {
    EXAM: 'badge-info',
    PROCEDURE: 'badge-warning',
    CONSULTATION: 'badge-secondary',
    BILLING: 'badge-success',
}

const formatMoney = (cents: number) =>
    (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })

/**
 * Busca do atendimento rápido, por NÚMERO ou por PACIENTE.
 *
 * Por número: o paciente liga citando "a guia 70921" e o operador não sabe se aquilo é
 * exame, procedimento, consulta ou cobrança — o mesmo número é procurado nas quatro. Os
 * ids COLIDEM entre as tabelas, então mais de um resultado é o normal.
 *
 * Por paciente: quem liga sem o número ("sou a Maria, marquei uns exames semana
 * passada") não tinha por onde ser atendido — a ficha do cliente não lista atendimentos.
 * Com homônimos, a aba pede a escolha do paciente (o CPF desambigua) antes de listar.
 *
 * Nas duas, resultado único pula a escolha e vai direto ao detalhe.
 */
export function SearchByNumberModal({ onClose }: Props) {
    const navigate = useNavigate()
    const [tab, setTab] = useState<'number' | 'client'>('number')
    const [term, setTerm] = useState('')
    const [loading, setLoading] = useState(false)
    const [searched, setSearched] = useState(false)
    const [hits, setHits] = useState<NumberSearchHit[]>([])

    // Só mostra o que o usuário pode abrir: sem permissão de leitura no recurso, o
    // resultado sequer aparece (clicar levaria a uma tela que ele não pode ver).
    const exams = useResourcePermissions('bookings', 'exams')
    const orders = useResourcePermissions('bookings', 'orders')
    const consultations = useResourcePermissions('bookings', 'consultations')
    const billings = useResourcePermissions('financial', 'billings')

    const canSee = (type: NumberSearchType): boolean => {
        if (type === 'EXAM') return exams.canRead
        if (type === 'PROCEDURE') return orders.canRead
        if (type === 'CONSULTATION') return consultations.canRead
        return billings.canRead
    }

    const goTo = (hit: NumberSearchHit) => {
        navigate(hit.url)
        onClose()
    }

    /** Troca de aba zera o resultado: manter a lista anterior sugeriria que ela veio da busca nova. */
    const switchTab = (next: 'number' | 'client') => {
        setTab(next)
        setTerm('')
        setHits([])
        setSearched(false)
    }

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault()
        const parsed = parseInt(term.trim(), 10)
        if (!Number.isFinite(parsed) || parsed <= 0) return

        setLoading(true)
        setSearched(false)
        try {
            const res = await searchByNumber(parsed)
            const found = (
                res.success && res.result ? res.result.hits : []
            ).filter(h => canSee(h.type))
            setHits(found)
            setSearched(true)

            // Resultado único: não faz o operador clicar de novo.
            if (found.length === 1) goTo(found[0])
        } catch {
            setHits([])
            setSearched(true)
        } finally {
            setLoading(false)
        }
    }

    const handleSearchClient = async (e: React.FormEvent) => {
        e.preventDefault()
        const query = term.trim()
        if (query.length < 2) return

        setLoading(true)
        setSearched(false)
        try {
            const res = await searchByClient(query)
            const found = (
                res.success && res.result ? res.result.hits : []
            ).filter(h => canSee(h.type))
            setHits(found)
            setSearched(true)

            // Mesmo atalho da busca por número: um só, abre direto.
            if (found.length === 1) goTo(found[0])
        } catch {
            setHits([])
            setSearched(true)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="modal modal-open" style={{ zIndex: 9999 }}>
            <div className="modal-box max-w-2xl">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="font-bold text-lg">Buscar atendimento</h3>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-circle"
                        onClick={onClose}
                        aria-label="Fechar"
                    >
                        <MdClose />
                    </button>
                </div>
                <div role="tablist" className="tabs tabs-boxed mb-4 mt-3">
                    <button
                        role="tab"
                        type="button"
                        className={`tab gap-2 ${tab === 'number' ? 'tab-active' : ''}`}
                        onClick={() => switchTab('number')}
                    >
                        <MdTag className="text-base" />
                        Por número
                    </button>
                    <button
                        role="tab"
                        type="button"
                        className={`tab gap-2 ${tab === 'client' ? 'tab-active' : ''}`}
                        onClick={() => switchTab('client')}
                    >
                        <MdPerson className="text-base" />
                        Por paciente
                    </button>
                </div>

                {tab === 'client' && (
                    <>
                        <p className="text-sm text-base-content/60 mb-4">
                            Informe o nome do paciente. Acento não precisa estar
                            certo, e o CPF pode ser digitado com ou sem
                            pontuação.
                        </p>

                        <form
                            onSubmit={handleSearchClient}
                            className="form-control w-full"
                        >
                            <label
                                className="label pb-1"
                                htmlFor="search-client"
                            >
                                <span className="label-text font-medium">
                                    Nome do paciente
                                </span>
                            </label>
                            <div className="flex gap-2">
                                <input
                                    id="search-client"
                                    type="text"
                                    autoFocus
                                    className="input input-bordered flex-1"
                                    placeholder="Ex.: Maria da Conceição"
                                    value={term}
                                    onChange={e => setTerm(e.target.value)}
                                />
                                <button
                                    type="submit"
                                    className="btn btn-secondary"
                                    disabled={loading || term.trim().length < 2}
                                >
                                    {loading ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdSearch className="text-lg" />
                                    )}
                                    Buscar
                                </button>
                            </div>
                        </form>

                        {searched && !loading && hits.length === 0 && (
                            <div className="mt-6 p-4 rounded-lg bg-base-200/60 text-sm text-center text-base-content/70">
                                Nenhum exame, procedimento, consulta ou cobrança
                                em aberto para esse paciente.
                            </div>
                        )}
                    </>
                )}

                {tab === 'number' && (
                    <>
                        <p className="text-sm text-base-content/60 mb-5">
                            Informe o número do documento. Ele é procurado em
                            exames, procedimentos, consultas e cobranças ao
                            mesmo tempo.
                        </p>

                        <form
                            onSubmit={handleSearch}
                            className="form-control w-full"
                        >
                            <label
                                className="label pb-1"
                                htmlFor="search-number"
                            >
                                <span className="label-text font-medium">
                                    Número do documento
                                </span>
                            </label>
                            <div className="flex gap-2">
                                <input
                                    id="search-number"
                                    type="number"
                                    min={1}
                                    autoFocus
                                    className="input input-bordered flex-1"
                                    placeholder="Ex.: 70921"
                                    value={term}
                                    onChange={e => setTerm(e.target.value)}
                                />
                                <button
                                    type="submit"
                                    className="btn btn-secondary"
                                    disabled={loading || !term.trim()}
                                >
                                    {loading ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdSearch className="text-lg" />
                                    )}
                                    Buscar
                                </button>
                            </div>
                        </form>

                        {searched && hits.length === 0 && !loading && (
                            <div className="mt-6 p-4 rounded-lg bg-base-200/60 text-sm text-center text-base-content/70">
                                Nenhum exame, procedimento, consulta ou cobrança
                                com esse número.
                            </div>
                        )}
                    </>
                )}

                {hits.length > 0 && (
                    <div className="mt-6 flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
                        <p className="text-xs text-base-content/50">
                            {hits.length}{' '}
                            {tab === 'client'
                                ? 'documentos deste paciente'
                                : 'resultados para este número'}{' '}
                            — escolha qual você quer abrir.
                        </p>
                        {hits.map(hit => (
                            <button
                                key={`${hit.type}-${hit.id}`}
                                type="button"
                                onClick={() => goTo(hit)}
                                className="w-full text-left p-3 rounded-lg border border-base-300 hover:border-secondary hover:bg-base-200/60 transition-colors"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5">
                                        {TYPE_ICON[hit.type]}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span
                                                className={`badge badge-sm ${TYPE_BADGE[hit.type]}`}
                                            >
                                                {hit.typeLabel}
                                            </span>
                                            <span className="font-medium truncate">
                                                {hit.title}
                                            </span>
                                            {hit.statusLabel && (
                                                <span className="badge badge-sm badge-ghost">
                                                    {hit.statusLabel}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1 text-sm text-base-content/70 flex flex-wrap gap-x-4 gap-y-0.5">
                                            {hit.clientName && (
                                                <span>
                                                    Paciente: {hit.clientName}
                                                </span>
                                            )}
                                            {hit.professionalName && (
                                                <span>
                                                    Profissional:{' '}
                                                    {hit.professionalName}
                                                </span>
                                            )}
                                            {hit.partnerName && (
                                                <span>
                                                    Parceiro: {hit.partnerName}
                                                </span>
                                            )}
                                            {hit.date && (
                                                <span>
                                                    {format(
                                                        new Date(hit.date),
                                                        "dd 'de' MMM 'de' yyyy",
                                                        { locale: ptBR }
                                                    )}
                                                </span>
                                            )}
                                            {hit.amount != null &&
                                                hit.amount > 0 && (
                                                    <span className="font-medium">
                                                        {formatMoney(
                                                            hit.amount
                                                        )}
                                                    </span>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </div>
    )
}
